// Module-level registry of provider shutdown functions.
//
// Providers register at module-load time (top-level `registerShutdown(...)`),
// matching the page-lifetime semantics of vendor singleton SDKs — once a
// provider's chunk loads, its shutdown stays available even if the React
// component unmounts (e.g., during route change before logout).
//
// authData.ts calls shutdownChat() on logout to fire all registered shutdowns.
const shutdowns: Array<() => unknown> = [];

// Returns an unregister function. Vendor-singleton providers (Intercom) can
// ignore it — their lifecycle is page-permanent. Tests and HMR-aware code use
// it to keep the registry from accumulating stale closures. Idempotent: calling
// the returned function twice is safe.
export function registerShutdown(fn: () => unknown): () => void {
  shutdowns.push(fn);
  return () => {
    const idx = shutdowns.indexOf(fn);
    if (idx >= 0) shutdowns.splice(idx, 1);
  };
}

/**
 * Fire all registered shutdowns in parallel. Per-fn try/catch ensures one
 * provider's failure cannot block others, and cannot block downstream
 * localStorage cleanup in clearAuthData.
 */
export async function shutdownChat(): Promise<void> {
  await Promise.allSettled(
    shutdowns.map(async (fn) => {
      try {
        await fn();
      } catch (err) {
        console.warn("[chat:shutdown]", err);
      }
    }),
  );
}
